import { RedoPhoneNumberInput } from "@redotech/redo-web/arbiter-components/input/redo-phone-number-input";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import type { Dispatch, SetStateAction } from "react";
import { memo } from "react";
import { MISSING_FIELD_LABELS, MISSING_FIELD_PLACEHOLDERS } from "./helpers";
import {
  getStaggeredContentRevealDelayMs,
  getTitleContentRevealDelayMs,
  StepMotionGroup,
  StepMotionItem,
} from "./step-motion";
import { StepTitle } from "./step-title";
import type {
  ConciergeBusinessInfo,
  ConciergeMissingMerchantField,
  ConciergePhoneReadiness,
  ConciergePhoneStatus,
  ConciergeProfileInfo,
  PhoneValidationField,
} from "./types";

export interface PhoneReadinessStepProps {
  animateItems?: boolean;
  stepNumber: number;
  effectivePhoneStatus: ConciergePhoneStatus;
  phoneReadinessLoadPending: boolean;
  phoneReadiness?: ConciergePhoneReadiness;
  missingMerchantFields: ConciergeMissingMerchantField[];
  businessInfo: ConciergeBusinessInfo;
  profileInfo: ConciergeProfileInfo;
  error: string | null;
  iMessageAvailable: boolean;
  validationErrors: Partial<Record<PhoneValidationField, string>>;
  onValidationFieldBlur(field: PhoneValidationField): void;
  setBusinessInfo: Dispatch<SetStateAction<ConciergeBusinessInfo>>;
  setProfileInfo: Dispatch<SetStateAction<ConciergeProfileInfo>>;
}

export const PhoneReadinessStep = memo(function PhoneReadinessStep({
  animateItems,
  stepNumber,
  effectivePhoneStatus,
  phoneReadinessLoadPending,
  phoneReadiness,
  missingMerchantFields,
  businessInfo,
  profileInfo,
  error,
  iMessageAvailable,
  validationErrors,
  onValidationFieldBlur,
  setBusinessInfo,
  setProfileInfo,
}: PhoneReadinessStepProps) {
  if (phoneReadinessLoadPending) {
    return <AnimatedSkeletonBlock height="280px" width="100%" />;
  }

  const needsProvisioning =
    effectivePhoneStatus === "none" || effectivePhoneStatus === "rejected";
  const title = "Set up your sending number";
  const contentRevealDelayMs = getTitleContentRevealDelayMs(title);
  const fieldDelay = (index: number) =>
    getStaggeredContentRevealDelayMs(title, index);

  return (
    <StepMotionGroup animateItems={animateItems}>
      <Flex dir="column" gap="lg">
        <StepMotionItem animateItems={animateItems}>
          <Text fontSize="sm" textColor="quaternary">
            Step {stepNumber}
          </Text>
        </StepMotionItem>
        <StepMotionItem animateItems={animateItems}>
          <StepTitle animate={animateItems} text={title} />
        </StepMotionItem>
        <StepMotionItem
          animateItems={animateItems}
          delayMs={contentRevealDelayMs}
        >
          <Text fontSize="lg" textColor="quaternary">
            {iMessageAvailable
              ? "Concierge will message iPhone shoppers over iMessage. For shoppers who are not on iPhone, we'll set up an approved texting number so Concierge can still reach them."
              : "Concierge needs an approved texting number before it can message customers. We'll collect the business details required to set up that number for your store."}
          </Text>
        </StepMotionItem>

        {effectivePhoneStatus === "pending" ? (
          <StepMotionItem
            animateItems={animateItems}
            delayMs={contentRevealDelayMs}
          >
            <Card title="Provisioning already in progress">
              <Text fontSize="sm" textColor="secondary">
                Your dedicated number is already being provisioned. You can
                finish the rest of Concierge setup now and we&apos;ll mark the
                agent live once the phone number is ready.
              </Text>
            </Card>
          </StepMotionItem>
        ) : null}

        {effectivePhoneStatus === "rejected" ? (
          <StepMotionItem
            animateItems={animateItems}
            delayMs={contentRevealDelayMs}
          >
            <Card title="We need a fresh provisioning attempt">
              <Text fontSize="sm" textColor="secondary">
                {phoneReadiness?.phoneNumberLastError ??
                  "Your last provisioning attempt didn’t go through. Review the business information and try again."}
              </Text>
            </Card>
          </StepMotionItem>
        ) : null}

        {needsProvisioning ? (
          <StepMotionGroup animateItems={animateItems}>
            <Flex dir="column" gap="lg">
              <StepMotionItem
                animateItems={animateItems}
                delayMs={fieldDelay(0)}
              >
                <RedoTextInput
                  errorText={validationErrors.legalEntityName}
                  fullWidth
                  label="Legal entity name"
                  onBlur={() => onValidationFieldBlur("legalEntityName")}
                  placeholder="Tony Ramirez LLC"
                  setValue={(value) =>
                    setBusinessInfo((current) => ({
                      ...current,
                      legalEntityName: value,
                    }))
                  }
                  state={validationErrors.legalEntityName ? "error" : "default"}
                  value={businessInfo.legalEntityName}
                />
              </StepMotionItem>
              <StepMotionItem
                animateItems={animateItems}
                delayMs={fieldDelay(1)}
              >
                <RedoTextInput
                  errorText={validationErrors.ein}
                  fullWidth
                  inputTypeHint="number"
                  label="EIN"
                  maxLength={9}
                  onBlur={() => onValidationFieldBlur("ein")}
                  placeholder="123456789"
                  setValue={(value) =>
                    setBusinessInfo((current) => ({
                      ...current,
                      ein: value.replace(/\D/g, ""),
                    }))
                  }
                  state={validationErrors.ein ? "error" : "default"}
                  value={businessInfo.ein}
                />
              </StepMotionItem>
              <StepMotionItem
                animateItems={animateItems}
                delayMs={fieldDelay(2)}
              >
                <RedoTextInput
                  fullWidth
                  label="Brand name"
                  placeholder="Tony Ramirez"
                  setValue={(value) =>
                    setBusinessInfo((current) => ({
                      ...current,
                      brandName: value,
                    }))
                  }
                  value={businessInfo.brandName ?? ""}
                />
              </StepMotionItem>
              <StepMotionItem
                animateItems={animateItems}
                delayMs={fieldDelay(3)}
              >
                <RedoTextInput
                  errorText={validationErrors.supportEmail}
                  fullWidth
                  inputTypeHint="email"
                  label="Support email"
                  onBlur={() => onValidationFieldBlur("supportEmail")}
                  placeholder="support@tonyramirez.com"
                  setValue={(value) =>
                    setBusinessInfo((current) => ({
                      ...current,
                      supportEmail: value,
                    }))
                  }
                  state={validationErrors.supportEmail ? "error" : "default"}
                  value={businessInfo.supportEmail ?? ""}
                />
              </StepMotionItem>

              {missingMerchantFields.map((field, index) => (
                <StepMotionItem
                  animateItems={animateItems}
                  delayMs={fieldDelay(4 + index)}
                  key={field}
                >
                  {field === "adminPhone" ? (
                    <RedoPhoneNumberInput
                      errorText={validationErrors.adminPhone}
                      label={MISSING_FIELD_LABELS[field] ?? field}
                      onBlur={() => onValidationFieldBlur("adminPhone")}
                      setValue={(value) =>
                        setProfileInfo((current) => ({
                          ...current,
                          adminPhone: value,
                        }))
                      }
                      size="sm"
                      state={validationErrors.adminPhone ? "error" : "default"}
                      value={profileInfo.adminPhone}
                    />
                  ) : (
                    <RedoTextInput
                      fullWidth
                      label={MISSING_FIELD_LABELS[field] ?? field}
                      placeholder={MISSING_FIELD_PLACEHOLDERS[field]}
                      setValue={(value) =>
                        setProfileInfo((current) => {
                          const profileField =
                            field as keyof ConciergeProfileInfo;
                          return { ...current, [profileField]: value };
                        })
                      }
                      value={
                        profileInfo[field as keyof ConciergeProfileInfo] ?? ""
                      }
                    />
                  )}
                </StepMotionItem>
              ))}
            </Flex>
          </StepMotionGroup>
        ) : null}

        {error ? (
          <StepMotionItem
            animateItems={animateItems}
            delayMs={contentRevealDelayMs}
          >
            <Text fontSize="sm" textColor="error">
              {error}
            </Text>
          </StepMotionItem>
        ) : null}
      </Flex>
    </StepMotionGroup>
  );
});
